import React, { useEffect, useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { auditService } from '@/services/audit';

interface Audit {
  id: number;
  audit_id: string;
  website_url: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
}

interface Issue {
  id: number;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  url: string;
  element: string;
  recommendation: string;
  status: 'pending' | 'fixed' | 'ignored';
  created_at: string;
  updated_at?: string;
}

interface AuditProgressProps {
  audit: Audit;
  onComplete: (issues: Issue[]) => void;
}

const AuditProgress: React.FC<AuditProgressProps> = ({ audit, onComplete }) => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Initializing audit...');

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case 'pending': return 0;
      case 'in_progress': return 50;
      case 'completed': return 100;
      case 'failed': return 0;
      default: return 0;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'in_progress':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Audit is queued and will start shortly...';
      case 'in_progress':
        return 'AI is analyzing your website...';
      case 'completed':
        return 'Audit completed successfully!';
      case 'failed':
        return 'Audit failed. Please try again.';
      default:
        return 'Processing...';
    }
  };

  // Polling logic
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;

    const pollProgress = async () => {
      try {
        const progressData = await auditService.getAuditProgress(audit.audit_id);
        // Update audit status if needed
        if (progressData.status !== audit.status) {
          // This would need to be handled by parent component
          // For now, we'll just log it
          console.log('Audit status changed:', progressData.status);
        }
      } catch (error) {
        console.error('Failed to get audit progress:', error);
      }
    };

    const pollIssues = async () => {
      try {
        const issuesData = await auditService.getAuditIssues(audit.audit_id);
        setIssues(issuesData.results || issuesData);
        onComplete(issuesData.results || issuesData);
      } catch (error) {
        console.error('Failed to get audit issues:', error);
      }
    };

    if (audit.status === 'pending' || audit.status === 'in_progress') {
      // Start polling every 30 seconds
      // intervalId = setInterval(pollProgress, 30000);
      intervalId = setInterval(pollProgress, 5000);
      
      // After 1.5 minutes, poll every 5 seconds
      timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        intervalId = setInterval(pollProgress, 5000);
      }, 5000); //original 90000
    }

    // If audit is completed, get issues
    if (audit.status === 'completed') {
      pollIssues();
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [audit.audit_id, audit.status, onComplete]);

  // Update progress and status message
  useEffect(() => {
    const progressPercentage = getProgressPercentage(audit.status);
    setProgress(progressPercentage);
    setStatusMessage(getStatusMessage(audit.status));
  }, [audit.status]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-2">Audit in Progress</h2>
        <p className="text-muted-foreground">
          Analyzing {audit.website_url}
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          {/* Status Badge */}
          <div className="flex items-center justify-center">
            <Badge className={`${getStatusColor(audit.status)} flex items-center gap-2`}>
              {getStatusIcon(audit.status)}
              {audit.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Status Message */}
          <div className="text-center">
            <p className="text-muted-foreground">{statusMessage}</p>
          </div>

          {/* Loading Steps */}
          {audit.status === 'in_progress' && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-center mb-3">
                Checking:
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  'SEO Analysis',
                  'Performance Check',
                  'Security Scan',
                  'Accessibility Test',
                  'Mobile Optimization',
                  'Content Quality'
                ].map((step, index) => (
                  <div 
                    key={step}
                    className={`p-2 rounded text-center ${
                      index < 3 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completion Message */}
          {audit.status === 'completed' && (
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 font-medium">
                Audit completed! Found {issues.length} issues to review.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AuditProgress;
